import type { CSSProperties } from 'react';

/**
 * LifecycleBar — a horizontal, numbered stage timeline for an entity's status
 * flow (e.g. a tax bill: no activity → received → processed → approved → paid).
 *
 * Stages up to and including `current` render as completed/active on the brand
 * color; later stages are muted. When a flow goes off-track (a voided bill),
 * pass `terminated` — every stage dims and the terminal state is called out.
 *
 * Scrolls horizontally inside its own container so a 5-stage bar never forces
 * the page wide on a phone.
 */

export interface LifecycleStage {
  key: string;
  label: string;
}

export interface LifecycleBarProps {
  stages: LifecycleStage[];
  current: string;
  /** When set, the flow ended off-track (e.g. voided): all stages dim and this
   *  label is called out as the terminal state. */
  terminated?: { label: string } | null;
}

export function LifecycleBar({ stages, current, terminated }: LifecycleBarProps) {
  const currentIndex = stages.findIndex((s) => s.key === current);

  return (
    <div style={{ overflowX: 'auto', padding: 'var(--sp-1) 2px' }}>
      <ol style={{
        display: 'flex', alignItems: 'flex-start', listStyle: 'none',
        margin: 0, padding: 0, minWidth: 'min-content',
      }}>
        {stages.map((stage, i) => {
          const done = !terminated && i < currentIndex;
          const isCurrent = !terminated && i === currentIndex;
          const active = done || isCurrent;
          const circle: CSSProperties = {
            width: 28, height: 28, borderRadius: '50%',
            display: 'grid', placeItems: 'center', flexShrink: 0,
            fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
            background: active ? 'var(--brand-primary)' : 'var(--color-card)',
            color: active ? 'var(--brand-on-primary)' : 'var(--color-muted)',
            border: '2px solid ' + (active ? 'var(--brand-primary)' : 'var(--color-border)'),
          };
          return (
            <li key={stage.key} style={{
              display: 'flex', alignItems: 'center',
              flex: i < stages.length - 1 ? 1 : '0 0 auto',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 6px' }}>
                <div style={circle}>{done ? '✓' : i + 1}</div>
                <span style={{
                  fontSize: 'var(--fs-xs)', textAlign: 'center', whiteSpace: 'nowrap',
                  color: active ? 'var(--color-ink)' : 'var(--color-muted)',
                  fontWeight: isCurrent ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                }}>{stage.label}</span>
              </div>
              {i < stages.length - 1 && (
                <div aria-hidden="true" style={{
                  flex: 1, height: 2, minWidth: 24, marginTop: 13,
                  background: done ? 'var(--brand-primary)' : 'var(--color-border)',
                }} />
              )}
            </li>
          );
        })}
      </ol>
      {terminated && (
        <div style={{
          marginTop: 'var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
          color: 'var(--color-status-red)',
        }}>{terminated.label}</div>
      )}
    </div>
  );
}
